import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { AlertCircle, Check, Copy, Mail, MessageCircle, Send, Share2, Users } from 'lucide-react';
import { functions } from '../lib/firebase';
import { Button, Input, Modal } from './ui';

type Created = {
  ok: true;
  code: string;
  name: string;
  existing: boolean;
  sent: boolean;
  trialEndsAt?: string;
};

const longDate = (d: Date) =>
  d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });

const callCreate = httpsCallable<{ name: string; organisation: string }, Created>(
  functions,
  'createOwnLab',
);
const callInvite = httpsCallable<
  { email: string },
  { ok: true; sent: boolean; reason: string | null }
>(functions, 'inviteToLab');

const pretty = (code: string) => `${code.slice(0, 4)} ${code.slice(4)}`;

const APP_URL = 'https://blisxbench.web.app';

/**
 * The words that travel with the code.
 *
 * Written to survive being pasted into a group chat with no other context: what
 * the app is, what to do with the number, and that the number is not a secret.
 * A bare eight digits in a chat window looks like a wrong number.
 */
function inviteText(labName: string, code: string): string {
  return [
    `Join ${labName || 'our lab'} on BlisxBench — it records instrument readings and exports them as a spreadsheet.`,
    '',
    `Lab code: ${pretty(code)}`,
    '',
    `Open ${APP_URL}, sign in, then use "Joining an existing lab?" and enter ${code}.`,
    'The code just groups our team so we can share projects. It is not a password.',
  ].join('\n');
}

/**
 * Make a lab, then hand the code to the people who should be in it.
 *
 * The code is not a password and is not a paywall — it groups accounts so that
 * colleagues appear in each other's sharing list instead of having to remember
 * one another's addresses. Said plainly here, because a screen that asks for a
 * code without explaining it invites people to assume it is a licence key.
 */
export default function LabSetup({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** Fires once a lab exists, so the caller can refresh what it shows. */
  onCreated?: (lab: { code: string; name: string }) => void;
}) {
  const [name, setName] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [created, setCreated] = useState<Created | null>(null);
  const [invitee, setInvitee] = useState('');
  const [invited, setInvited] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  /** The OS share sheet, where there is one — a phone, mostly. */
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The trial clock starts on creation, so the owner must say they know. */
  const [understood, setUnderstood] = useState(false);
  const trialEnds = longDate(new Date(Date.now() + 30 * 86_400_000));

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (name.trim().length < 2 || !understood || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await callCreate({ name: name.trim(), organisation: organisation.trim() });
      setCreated(res.data);
      onCreated?.({ code: res.data.code, name: res.data.name });
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not create the lab.');
    } finally {
      setBusy(false);
    }
  }

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    if (!invitee.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await callInvite({ email: invitee.trim() });
      setInvited((prev) => [...prev, invitee.trim()]);
      setInvitee('');
      if (!res.data.sent) {
        setError(
          res.data.reason ??
            'Email is not configured, so nothing was sent — pass the code on yourself.',
        );
      }
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not send that invitation.');
    } finally {
      setBusy(false);
    }
  }

  function copy(code: string) {
    navigator.clipboard?.writeText(code).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
      },
      () => setError('Could not copy — select the code and copy it manually.'),
    );
  }

  return (
    <Modal open={open} onClose={onClose} title={created ? 'Your organisation code' : 'Create your organisation'}>
      {!created ? (
        <form onSubmit={create} className="space-y-3">
          <p className="text-sm text-ink-400">
            Your organisation holds your team, its projects and its subscription. You become its
            owner and get a code your colleagues use to join.
          </p>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Lab name"
            autoFocus
            aria-label="Lab name"
          />
          <Input
            value={organisation}
            onChange={(e) => setOrganisation(e.target.value)}
            placeholder="Organisation (optional)"
            aria-label="Organisation"
          />
          <div className="rounded-lg border border-amber-800 bg-amber-950 p-3 text-sm text-amber-200">
            <p className="flex gap-2 font-semibold">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              Your 30-day free trial starts as soon as you create the organisation
            </p>
            <p className="mt-1">
              It runs until {trialEnds}, whether or not you use it. The trial gives full access with a
              small allowance: 50 AI credits and 30 minutes of spoken notes. Subscribe at any time for
              the full allowance.
            </p>
            <label className="mt-2 flex items-start gap-2">
              <input
                type="checkbox"
                checked={understood}
                onChange={(e) => setUnderstood(e.target.checked)}
                className="mt-1"
              />
              <span>I understand the trial starts today.</span>
            </label>
          </div>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            loading={busy}
            disabled={name.trim().length < 2 || !understood}
            icon={<Users size={18} />}
          >
            Create organisation
          </Button>
        </form>
      ) : (
        <div className="space-y-4">
          {created.existing && (
            <p className="rounded-lg border border-sky-800 bg-sky-950 p-3 text-sm text-sky-200">
              You already had a lab, so here it is again rather than a second one.
            </p>
          )}

          {created.trialEndsAt && (
            <p className="rounded-lg border border-amber-800 bg-amber-950 p-3 text-sm text-amber-200">
              Your free trial has started. It ends on {longDate(new Date(created.trialEndsAt))}.
            </p>
          )}

          <div className="rounded-xl border border-ink-700 bg-ink-900 p-4 text-center">
            <p className="text-sm text-ink-400">{created.name}</p>
            <p className="mt-2 font-mono text-3xl font-bold tracking-widest text-accent-400">
              {pretty(created.code)}
            </p>
            <p className="mt-1 text-xs text-ink-400">enter as {created.code}</p>
            <Button
              size="sm"
              variant="secondary"
              className="mt-3"
              icon={copied ? <Check size={13} /> : <Copy size={13} />}
              onClick={() => copy(created.code)}
            >
              {copied ? 'Copied' : 'Copy code'}
            </Button>
          </div>

          <p className="text-sm text-ink-400">
            Your teammates enter this once on the sign-in screen, under &ldquo;Joining an existing
            lab?&rdquo;. It identifies the lab — it is not a password, and it does not replace
            signing in.
          </p>

          <div>
            <p className="field-label mb-2">Send it</p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                icon={<MessageCircle size={14} />}
                onClick={() =>
                  window.open(
                    `https://wa.me/?text=${encodeURIComponent(
                      inviteText(created.name, created.code),
                    )}`,
                    '_blank',
                    'noopener',
                  )
                }
              >
                WhatsApp
              </Button>
              <Button
                size="sm"
                variant="secondary"
                icon={<Send size={14} />}
                onClick={() =>
                  window.open(
                    `https://t.me/share/url?url=${encodeURIComponent(APP_URL)}&text=${encodeURIComponent(
                      inviteText(created.name, created.code),
                    )}`,
                    '_blank',
                    'noopener',
                  )
                }
              >
                Telegram
              </Button>
              {canShare && (
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<Share2 size={14} />}
                  onClick={() => {
                    void navigator
                      .share({
                        title: 'BlisxBench lab code',
                        text: inviteText(created.name, created.code),
                      })
                      .catch(() => {
                        // A cancelled share sheet is not an error worth showing.
                      });
                  }}
                >
                  More…
                </Button>
              )}
            </div>
          </div>

          <form onSubmit={invite} className="space-y-2">
            <label className="field-label" htmlFor="invite-email">
              Email it to someone
            </label>
            <div className="flex gap-2">
              <Input
                id="invite-email"
                type="email"
                value={invitee}
                onChange={(e) => setInvitee(e.target.value)}
                placeholder="colleague@example.com"
                autoCapitalize="none"
                spellCheck={false}
                className="flex-1"
              />
              <Button
                type="submit"
                variant="secondary"
                loading={busy}
                disabled={!invitee.trim()}
                icon={<Send size={15} />}
              >
                Send
              </Button>
            </div>
          </form>

          {invited.length > 0 && (
            <ul className="space-y-1">
              {invited.map((who) => (
                <li key={who} className="flex items-center gap-1.5 text-xs text-emerald-300">
                  <Mail size={12} />
                  Sent to {who}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {error && (
        <p className="mt-4 flex gap-2 rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-200">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </p>
      )}
    </Modal>
  );
}
